import { AxiosError } from 'axios'
import { refreshToken } from '@/queries/auth'
import { Mutation, Query } from '@tanstack/react-query'
import userStore from '@/stores/user-store'

let isRedirecting = false
let isRefreshing = false
let failedQueue: {
  query?: Query<unknown, unknown, unknown>
  mutation?: Mutation<unknown, unknown, unknown, unknown>
  variables?: unknown
}[] = []

// Add your excluded URL substrings here
const excludedUrls = [
  '/auth/reset-password'
]

const shouldSkipRefresh = (url?: string) => {
  if (!url) return false
  return excludedUrls.some(excluded => url.includes(excluded))
}

const errorHandler = (
  error: unknown,
  query?: Query<unknown, unknown, unknown>,
  mutation?: Mutation<unknown, unknown, unknown, unknown>,
  variables?: unknown
) => {
  const axiosError = error as AxiosError
  const response = axiosError.response
  const requestUrl = axiosError.config?.url

  if (!response) {
    console.error("errorHandler: Unknown error:", error)
    return
  }

  const { status, data }: any = response
  console.log(`errorHandler: Intercepted an error with status: ${status} from ${requestUrl}`);

  // Skip refresh token logic if URL is excluded
  if (shouldSkipRefresh(requestUrl)) {
    console.warn(`errorHandler: Skipping refresh token for excluded URL: ${requestUrl}`)
    return
  }

  if (status === 401) {
    console.log('errorHandler: Status is 401, attempting to refresh token.');
    if (mutation) refreshTokenAndRetry(undefined, mutation, variables)
    else refreshTokenAndRetry(query)
  } else {
    console.error(`errorHandler: Error with status ${status}`, data?.message)
  }
}

export const queryErrorHandler = (
  error: unknown,
  query: Query<unknown, unknown, unknown>
) => {
  console.log(error)
  errorHandler(error, query)
}

export const mutationErrorHandler = (
  error: unknown,
  variables: unknown,
  context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>
) => {
  console.log(error)
  errorHandler(error, undefined, mutation, variables)
}

const processFailedQueue = (success: boolean) => {
  console.log(`processFailedQueue: Processing queue. Success: ${success}`);
  if (success) {
    failedQueue.forEach(({ query, mutation, variables }: any) => {
      if (mutation) {
        console.log('processFailedQueue: Retrying mutation.');
        mutation.execute(variables);
      } else if (query) {
        console.log('processFailedQueue: Retrying query.');
        query.fetch();
      }
    });
  }

  failedQueue = [];
  isRefreshing = false;
};

const refreshTokenAndRetry = async (
  query?: Query<unknown, unknown, unknown>,
  mutation?: Mutation<unknown, unknown, unknown, unknown>,
  variables?: unknown
) => {
  console.log('refreshTokenAndRetry: Attempting to refresh token');
  failedQueue.push({ query, mutation, variables });

  if (!isRefreshing) {
    isRefreshing = true;
    try {
      console.log('refreshTokenAndRetry: Calling refreshToken()');
      const { access_token, refresh_token } = await refreshToken();
      console.log('refreshTokenAndRetry: Token refreshed successfully.');
      userStore.setState(state => ({ ...state, token: access_token, refresh_token: refresh_token }));
      processFailedQueue(true);
    } catch (e) {
      console.error('refreshTokenAndRetry: Failed to refresh token.', e);
      processFailedQueue(false);
      if (!isRedirecting) {
        isRedirecting = true;
        window.location.href = `${import.meta.env.VITE_APP_AUTH_URL}/?logout=true&redirectUrl=${location.protocol}//${location.host}`;
      }
    }
  } else {
    console.log('refreshTokenAndRetry: Refresh token process already in progress, adding to queue.');
  }
};
